import React, { useEffect, useMemo, useState } from "react";
import GenericModal from "../../Ui/Modal";
import ButtonComponent from "../../Ui/Button";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import pdfWorker from "pdfjs-dist/build/pdf.worker.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

const MOBILE_BREAKPOINT = 768;

const PdfViewerModal = ({
  open,
  onClose,
  documentTitle = "Document Viewer",
  documentUrl,
  updatedAt,
}) => {
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pageDimensions, setPageDimensions] = useState({ height: 900, width: 780 });
  const documentFile = useMemo(() => documentUrl || null, [documentUrl]);
  const isMobile = viewportWidth <= MOBILE_BREAKPOINT;
  const pageWidth = useMemo(() => {
    if (!isMobile) return pageDimensions.width;
    const paddingOffset = 48;
    const maxWidth = Math.max(viewportWidth - paddingOffset, 320);
    return Math.min(pageDimensions.width, maxWidth);
  }, [isMobile, pageDimensions.width, viewportWidth]);
  const scaledHeight = useMemo(() => {
    if (!pageDimensions.width) return pageDimensions.height;
    const ratio = pageWidth / pageDimensions.width;
    return Math.max(Math.round(pageDimensions.height * ratio), 400);
  }, [pageDimensions.height, pageDimensions.width, pageWidth]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!open) {
      setNumPages(null);
      setPageNumber(1);
      setError(null);
      setIsPageLoading(false);
    }
  }, [open, documentUrl]);

  const handleLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
    setPageNumber(1);
    setError(null);
    setIsPageLoading(false);
  };

  const handlePrev = () => {
    if (pageNumber <= 1) return;
    setIsPageLoading(true);
    setPageNumber((prev) => prev - 1);
  };

  const handleNext = () => {
    if (!numPages || pageNumber >= numPages) return;
    setIsPageLoading(true);
    setPageNumber((prev) => prev + 1);
  };
  const handlePageRender = ({ height, width }) => {
    setPageDimensions((prevDimensions) => ({
      height: Math.max(height || prevDimensions.height, 400),
      width: width || prevDimensions.width,
    }));
    setIsPageLoading(false);
  };

  const footer = (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 w-full">
      <div className="text-sm text-[#595959]">
        {numPages ? `Page ${pageNumber} of ${numPages}` : "No pages loaded"}
      </div>
      <div className="flex gap-2">
        <ButtonComponent onClick={handlePrev} disabled={pageNumber <= 1}>
          Previous
        </ButtonComponent>
        <ButtonComponent onClick={handleNext} disabled={!numPages || pageNumber >= numPages}>
          Next
        </ButtonComponent>
      </div>
    </div>
  );

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title={documentTitle}
      footer={footer}
      maxWidth="900px"
    >
      {!documentUrl ? (
        <p className="text-[#595959] text-sm">No document selected.</p>
      ) : (
        <div
          className="w-full overflow-auto bg-[#F9F9F9] rounded-xl relative"
          style={{ minHeight: scaledHeight + 32 }}
        >
          <Document
            file={documentFile}
            onLoadSuccess={handleLoadSuccess}
            onLoadError={(err) => setError(err?.message || "Unable to load PDF")}
            loading={<p className="p-6 text-center text-sm text-[#595959]">Loading PDF…</p>}
            error={
              <p className="p-6 text-center text-sm text-red-600">
                {error || "Unable to load this PDF."}
              </p>
            }
          >
            {numPages ? (
              <>
                {isPageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                    <p className="text-sm text-[#595959]">Loading page…</p>
                  </div>
                )}
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth}
                  onRenderSuccess={handlePageRender}
                />
              </>
            ) : error ? null : (
              <p className="p-6 text-center text-sm text-[#595959]">Preparing preview…</p>
            )}
          </Document>
        </div>
      )}
      {updatedAt ? (
        <p className="text-xs text-[#8C8C8C] mt-3">
          Last updated {new Date(updatedAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      ) : null}
    </GenericModal>
  );
};

export default PdfViewerModal;
